"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import {
	CLICK_ID_PARAMS,
	type FirstTouchKey,
} from "@/lib/analytics/acquisition-touch";
import { classifyChannel } from "@/lib/analytics/channel";
import {
	persistFirstTouchAttribution,
	readAttribution,
} from "./posthog-provider";

/** First click-id param present, scanned in `CLICK_ID_PARAMS` priority order. */
function extractClickId(params: URLSearchParams): {
	clickIdKind: string | null;
	clickId: string | null;
} {
	for (const kind of CLICK_ID_PARAMS) {
		const value = params.get(kind);
		if (value && value.length > 0 && value.length <= 200) {
			return { clickIdKind: kind, clickId: value };
		}
	}
	return { clickIdKind: null, clickId: null };
}

type TouchBeaconBody = {
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmTerm: string | null;
	utmContent: string | null;
	clickIdKind: string | null;
	clickId: string | null;
	referrerHost: string | null;
	landingPath: string;
	hasReferralCode: boolean;
};

/**
 * Unconditional acquisition capture (plan P0.3(b)) — decoupled from PostHog
 * init. Renders as a sibling of `<PostHogPageView />` inside the same
 * `<Suspense>` in `posthog-provider.tsx`, so a visitor is captured even
 * before (or without ever) reaching PostHog's `ready` gate: an EEA/UK/CH
 * visitor who hasn't opted into "analytics" yet, or anyone who bounces
 * before that resolves. The consent decision for the DURABLE server-side row
 * moves server-side too (P0.3(c), `POST /api/attribution/touch`) — this
 * component always fires the beacon; the route decides whether to store
 * anything.
 *
 * Owns: `readAttribution` (self-referral-suppressed utm/referrer signal,
 * P0.3(a)) → the widened `ld_attr` first-touch cookie (P0.3(d),
 * `FIRST_TOUCH_KEYS`) → the beacon POST. `PostHogPageView` keeps only the
 * PostHog `register`/`setPersonProperties`/`capture` work.
 */
export function AttributionCapture() {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (!pathname) return;
		const params = new URLSearchParams(searchParams.toString());
		const attribution = readAttribution(params);
		const { clickIdKind, clickId } = extractClickId(params);
		const search = params.toString();
		const landingPath = search ? `${pathname}?${search}` : pathname;
		const hasReferralCode = Boolean(params.get("ref"));

		const channel = classifyChannel({
			utmSource: attribution.utm_source,
			utmMedium: attribution.utm_medium,
			utmCampaign: attribution.utm_campaign,
			clickIdKind,
			referrerHost: attribution.referrer_host,
			hasReferralCode,
		});

		// Widened first-touch cookie (P0.3(d)). `channel` is omitted for a pure
		// direct/untagged visit so the cookie keeps its existing meaning — "the
		// first touch that actually carried acquisition signal" — unchanged by
		// this PR; every other field is a plain presence check inside
		// `persistFirstTouchAttribution` itself.
		const cookiePayload: Partial<Record<FirstTouchKey, string>> = {
			utm_source: attribution.utm_source,
			utm_medium: attribution.utm_medium,
			utm_campaign: attribution.utm_campaign,
			utm_term: attribution.utm_term,
			utm_content: attribution.utm_content,
			referrer_host: attribution.referrer_host,
			ai_referrer: attribution.ai_referrer,
			click_id_kind: clickIdKind ?? undefined,
			click_id: clickId ?? undefined,
			landing_path: landingPath,
			channel: channel === "direct" ? undefined : channel,
		};
		persistFirstTouchAttribution(cookiePayload);

		// Durable server-side touch row — mirrors the house beacon idiom
		// (src/lib/content/click-beacon.ts) exactly: sessionStorage dedupe +
		// fetch(..., { keepalive: true }).catch(() => {}), never
		// navigator.sendBeacon (no consent/geo control over that send path).
		// Dedupe key is landingPath-scoped (plan P0.3(c)) — the 'last' touch is
		// worth re-sending on a genuinely new page; the 'first' touch is
		// idempotent server-side (`onConflictDoNothing`) regardless.
		try {
			const dedupeKey = `attr_touch:${landingPath}`;
			if (window.sessionStorage.getItem(dedupeKey)) return;
			window.sessionStorage.setItem(dedupeKey, "1");
			const body: TouchBeaconBody = {
				utmSource: attribution.utm_source ?? null,
				utmMedium: attribution.utm_medium ?? null,
				utmCampaign: attribution.utm_campaign ?? null,
				utmTerm: attribution.utm_term ?? null,
				utmContent: attribution.utm_content ?? null,
				clickIdKind,
				clickId,
				referrerHost: attribution.referrer_host ?? null,
				landingPath,
				hasReferralCode,
			};
			void fetch("/api/attribution/touch", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				keepalive: true,
				body: JSON.stringify(body),
			}).catch(() => {});
		} catch {
			// sessionStorage blocked / private mode — skip silently
		}
	}, [pathname, searchParams]);

	return null;
}
